import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Lifebuoy, PaperPlaneTilt, ArrowClockwise, LockKey, WarningCircle, Megaphone, Flask, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";
import { money } from "../lib/pricing";
import RecoveryOutreachDialog from "./RecoveryOutreachDialog";
import DunningDemoDialog from "./DunningDemoDialog";
import RecoveryOutcomesList from "./RecoveryOutcomesList";
import WinbackResultsCard from "./WinbackResultsCard";
import OwnerReplyInbox from "./OwnerReplyInbox";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never");
const ago = (iso) => { const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000); return d <= 0 ? "today" : d === 1 ? "1 day ago" : `${d} days ago`; };

export default function PaymentRecoveryPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [outreach, setOutreach] = useState(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [clearing, setClearing] = useState("");
  const closeOutreach = useCallback(() => setOutreach(null), []);

  const clearSimulation = async (r) => {
    setClearing(r.tenant_id);
    try {
      await API.delete(`/platform/payment-recovery/simulate/${r.tenant_id}`);
      toast.success(`Simulation cleared for ${r.company_name || r.slug}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't clear the simulation"); } finally { setClearing(""); }
  };

  const runAutoChase = async () => {
    try {
      const { data } = await API.post("/platform/payment-recovery/auto-chase/run");
      toast.success(data.sent ? `Auto-chase sent ${data.sent} reminder(s): ${data.tenants.join(", ")}` : data.skipped === "disabled" ? "Auto-chase is switched off" : "Nobody needs a follow-up right now");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Auto-chase failed"); }
  };

  const load = useCallback(() => API.get("/platform/payment-recovery").then(({ data: d }) => setData(d)).catch(() => toast.error("Couldn't load payment recovery")).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!loading && window.location.hash === "#payment-recovery") document.getElementById("payment-recovery")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [loading]);

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      const { data: cfg } = await API.put("/platform/payment-recovery/settings", patch);
      setData((d) => ({ ...d, settings: cfg }));
      if (Object.keys(patch).some((k) => k.startsWith("auto_chase"))) {
        toast.success(cfg.auto_chase_enabled ? `Auto-chase on — ${cfg.auto_chase_days_before_lock} day(s) before read-only, ${cfg.auto_chase_gap_days}-day gap` : "Auto-chase off");
      } else {
        toast.success(cfg.enabled ? `Weekly digest on — every ${WEEKDAYS[cfg.weekday]} at ${String(cfg.hour).padStart(2, "0")}:00` : "Weekly digest off");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); } finally { setSaving(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data: r } = await API.post("/platform/payment-recovery/send");
      toast.success(`Digest sent to ${r.sent} platform admin${r.sent === 1 ? "" : "s"} — ${r.counts.past_due} past due, ${r.counts.expired_trials} expired trials`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't send"); } finally { setSending(false); }
  };

  const s = data?.settings || {};
  const rows = data?.past_due || [];
  const trials = data?.expired_trials || [];

  return (
    <div id="payment-recovery" className="border border-border rounded-lg overflow-hidden mt-6" data-testid="payment-recovery-panel">
      <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Lifebuoy size={14} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold font-[Chivo]">Payment Recovery</h3>
          {data && <Badge variant="secondary" className="text-[10px]" data-testid="payment-recovery-counts">{data.counts.past_due} past due · {data.counts.locked} locked · {money(data.mrr_at_risk, { compact: true })}/mo at risk</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-muted-foreground" data-testid="payment-recovery-last-sent">Last digest: {fmtWhen(s.last_sent_at)}</span>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="payment-recovery-refresh"><ArrowClockwise size={13} /></Button>
          <Button size="sm" variant="outline" onClick={() => setDemoOpen(true)} data-testid="payment-recovery-demo-btn"><Flask size={13} className="mr-1" /> Demo</Button>
          <Button size="sm" onClick={sendNow} disabled={sending} data-testid="payment-recovery-send-btn"><PaperPlaneTilt size={13} className="mr-1" /> {sending ? "Sending…" : "Send digest now"}</Button>
        </div>
      </div>

      <div className="px-4 py-2.5 border-b border-border flex flex-wrap items-center gap-3 text-xs" data-testid="payment-recovery-settings">
        <label className="flex items-center gap-2 cursor-pointer">
          <Switch checked={!!s.enabled} disabled={saving || !data} onCheckedChange={(v) => saveSettings({ enabled: v })} data-testid="payment-recovery-enabled" />
          Weekly email to platform admins
        </label>
        <span className="text-muted-foreground">every</span>
        <select value={s.weekday ?? 0} disabled={saving || !data} onChange={(e) => saveSettings({ weekday: parseInt(e.target.value, 10) })} className="h-7 rounded-md border border-input bg-background px-2 text-xs" data-testid="payment-recovery-weekday">
          {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
        <span className="text-muted-foreground">at</span>
        <select value={s.hour ?? 8} disabled={saving || !data} onChange={(e) => saveSettings({ hour: parseInt(e.target.value, 10) })} className="h-7 rounded-md border border-input bg-background px-2 text-xs" data-testid="payment-recovery-hour">
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
        </select>
        <span className="text-muted-foreground">{s.timezone || "Europe/London"} · skipped automatically when nothing is past due</span>
      </div>

      <div className="px-4 py-2.5 border-b border-border flex flex-wrap items-center gap-3 text-xs" data-testid="payment-recovery-auto-chase">
        <label className="flex items-center gap-2 cursor-pointer">
          <Switch checked={s.auto_chase_enabled !== false} disabled={saving || !data} onCheckedChange={(v) => saveSettings({ auto_chase_enabled: v })} data-testid="payment-recovery-auto-chase-enabled" />
          <Robot size={13} className="text-muted-foreground" /> Auto-chase owners
        </label>
        <select value={s.auto_chase_days_before_lock ?? 3} disabled={saving || !data} onChange={(e) => saveSettings({ auto_chase_days_before_lock: parseInt(e.target.value, 10) })} className="h-7 rounded-md border border-input bg-background px-2 text-xs" data-testid="payment-recovery-auto-chase-days">
          {Array.from({ length: 13 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d} day{d === 1 ? "" : "s"}</option>)}
        </select>
        <span className="text-muted-foreground">before read-only, if nobody has chased them in the last</span>
        <select value={s.auto_chase_gap_days ?? 3} disabled={saving || !data} onChange={(e) => saveSettings({ auto_chase_gap_days: parseInt(e.target.value, 10) })} className="h-7 rounded-md border border-input bg-background px-2 text-xs" data-testid="payment-recovery-auto-chase-gap">
          {Array.from({ length: 14 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d} day{d === 1 ? "" : "s"}</option>)}
        </select>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={runAutoChase} disabled={!data} data-testid="payment-recovery-auto-chase-run">Run now</Button>
        {s.last_auto_chase_at && <span className="text-[10px] text-muted-foreground" data-testid="payment-recovery-auto-chase-last">Last auto-chase {fmtWhen(s.last_auto_chase_at)}{s.last_auto_chase?.sent?.length ? ` → ${s.last_auto_chase.sent.join(", ")}` : ""}</span>}
      </div>

      {loading ? <p className="p-4 text-xs text-muted-foreground">Loading…</p> : (
        <div className="divide-y divide-border">
          {rows.length === 0 && <p className="px-4 py-3 text-xs text-muted-foreground" data-testid="payment-recovery-empty">No past-due workspaces — nothing to chase.</p>}
          {rows.map((r) => (
            <div key={r.tenant_id} className="px-4 py-2.5 grid grid-cols-1 md:grid-cols-[1.4fr_1.2fr_1fr_1fr_.7fr_auto] gap-2 items-center text-xs" data-testid={`payment-recovery-row-${r.slug || r.tenant_id}`}>
              <div>
                <Link to="/platform/tenants" className="font-semibold hover:underline">{r.company_name || r.slug}</Link>
                {r.simulated && <Badge variant="outline" className="ml-1.5 text-[9px] border-violet-400 text-violet-700 align-middle" data-testid={`payment-recovery-simulated-${r.slug || r.tenant_id}`}>SIMULATED</Badge>}
                <p className="text-[10px] text-muted-foreground capitalize">{r.plan} · {r.seats} seats · {r.cycle}{r.last_active_at ? ` · active ${fmtDay(r.last_active_at)}` : ""}</p>
              </div>
              <div className="truncate">
                <p>{r.owner_name || "—"}</p>
                {r.owner_email && <a href={`mailto:${r.owner_email}`} className="text-[10px] text-primary hover:underline" data-testid="payment-recovery-owner-email">{r.owner_email}</a>}
              </div>
              <div>
                <p className="font-semibold">{money(r.amount)}</p>
                <p className="text-[10px] text-muted-foreground">declined {fmtDay(r.failed_at)} · {r.attempt_count || 1}× · retry {fmtDay(r.next_attempt_at)}</p>
              </div>
              <div data-testid="payment-recovery-lock">
                {r.locked ? <Badge className="bg-red-600 text-white text-[10px] gap-1"><LockKey size={10} weight="fill" /> Locked since {fmtDay(r.lock_at)}</Badge>
                  : <Badge variant="outline" className={`text-[10px] gap-1 ${r.days_to_lock <= 3 ? "border-red-400 text-red-700" : "border-amber-400 text-amber-700"}`}><WarningCircle size={10} /> {r.days_to_lock} day{r.days_to_lock === 1 ? "" : "s"} to read-only</Badge>}
              </div>
              <div className="text-right font-semibold">{money(r.mrr, { compact: true })}<span className="text-[10px] text-muted-foreground font-normal">/mo</span></div>
              <div className="text-right">
                <Button size="sm" variant={r.chased_at ? "outline" : "default"} className="h-7 text-xs" disabled={!r.owner_email} onClick={() => setOutreach({ kind: "chase", tenantId: r.tenant_id })} data-testid={`payment-recovery-chase-${r.slug || r.tenant_id}`}>
                  <Megaphone size={12} className="mr-1" /> {r.chased_at ? "Chase again" : "Chase"}
                </Button>
                {r.chased_at && <p className="text-[10px] text-muted-foreground mt-0.5 whitespace-nowrap" data-testid={`payment-recovery-chased-${r.slug || r.tenant_id}`}>Chased {ago(r.chased_at)} · {r.chase_count}×{r.chased_by === "auto-chase" ? " · auto" : ""}</p>}
                {r.simulated && <button type="button" className="text-[10px] text-violet-700 hover:underline mt-0.5" disabled={clearing === r.tenant_id} onClick={() => clearSimulation(r)} data-testid={`payment-recovery-clear-sim-${r.slug || r.tenant_id}`}>{clearing === r.tenant_id ? "Clearing…" : "End simulation"}</button>}
              </div>
            </div>
          ))}
          {trials.length > 0 && (
            <div className="px-4 py-2.5" data-testid="payment-recovery-trials">
              <p className="text-[10px] font-semibold text-muted-foreground mb-1.5">EXPIRED TRIALS — NEVER SUBSCRIBED ({trials.length})</p>
              <div className="flex flex-wrap gap-1.5">
                {trials.slice(0, 12).map((t) => (
                  <span key={t.tenant_id} className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-[11px]" title={`${t.owner_name} · ${t.owner_email} · ${t.users} users · ${t.jobs} jobs`} data-testid={`payment-recovery-trial-${t.slug || t.tenant_id}`}>
                    {t.company_name || t.slug}<span className="text-muted-foreground">· {t.days_since_expiry}d ago</span>
                    {t.winback_at && <span className="text-[10px] text-emerald-700" data-testid={`payment-recovery-winback-sent-${t.slug || t.tenant_id}`}>· sent {ago(t.winback_at)}{t.winback_count > 1 ? ` (${t.winback_count}×)` : ""}</span>}
                    {t.owner_email && <button type="button" className="text-primary hover:underline font-semibold" onClick={() => setOutreach({ kind: "winback", tenantId: t.tenant_id })} data-testid={`payment-recovery-winback-${t.slug || t.tenant_id}`}>{t.winback_at ? "win back again" : "win back"}</button>}
                  </span>
                ))}
                {trials.length > 12 && <span className="text-[11px] text-muted-foreground">+{trials.length - 12} more</span>}
              </div>
            </div>
          )}
        </div>
      )}
      {!loading && <RecoveryOutcomesList items={data?.recent_outreach || []} />}
      {!loading && <WinbackResultsCard onDefaultChanged={load} />}
      {!loading && <OwnerReplyInbox />}
      <RecoveryOutreachDialog open={!!outreach} kind={outreach?.kind || "chase"} tenantId={outreach?.tenantId} onClose={closeOutreach} onSent={load} />
      <DunningDemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} onDone={load} />
    </div>
  );
}
